import { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { apiUrl, authHeaders } from './shell';
import {
  createSessionGuard,
  guardFetch,
  type CurrentIdentity,
  type SessionGuard,
} from './session-guard';

/**
 * The server's current identity for this browser's session: ONE coherent read, the existing
 * GET /api/v1/company (the user and the company come from the same answer). `/auth/me` is read only for
 * the display name in the notice, never to decide a match.
 */
async function readIdentity(raw: typeof fetch): Promise<CurrentIdentity | null> {
  const get = (path: string) =>
    raw(apiUrl(path), { credentials: 'same-origin', headers: authHeaders() });
  try {
    const company = await get('/api/v1/company');
    if (company.status === 401)
      return { userId: null, companyId: null, account: null, company: null };
    if (!company.ok) return null;
    const body = await company.json();
    const userId =
      typeof body?.userId === 'string'
        ? body.userId
        : typeof body?.membership?.userId === 'string'
          ? body.membership.userId
          : null;
    if (!userId) return null; // no coherent user in the answer: unknown, never a mix with another read
    let account: string | null = null;
    try {
      const me = await get('/api/v1/auth/me');
      if (me.ok) {
        const user = (await me.json())?.user;
        if (user?.id === userId) account = user.displayName ?? user.email ?? null;
      }
    } catch {
      /* the label is optional */
    }
    return {
      userId,
      companyId: typeof body?.company?.id === 'string' ? body.company.id : null,
      account,
      company: typeof body?.company?.name === 'string' ? body.company.name : null,
    };
  } catch {
    return null;
  }
}

/** The page's own fetch, captured before any guard patches it. */
let originalFetch: typeof fetch | null = null;
const rawFetch = (): typeof fetch => (originalFetch ??= globalThis.fetch.bind(globalThis));
/** Tests that install their own page fetch: capture it afresh on the next read. */
export function resetPageFetchForTests(): void {
  originalFetch = null;
}

/** The guard serving the signed-in tab, and a fetch BOUND to it (never to a later guard). */
export type SessionGuardHandle = { guard: SessionGuard; fetch: typeof fetch };

const SessionGuardContext = createContext<SessionGuardHandle | null>(null);
export const SessionGuardProvider = SessionGuardContext.Provider;

/**
 * The fetch an ACTION (a Chat turn with its resume and recovery, a paged read, a terminal client) must
 * use for its whole life. It is bound to the guard of the identity the tab showed when the action
 * began: when that identity is retired every call ends with SessionChangedError (terminal), and it never
 * follows a replacement guard. Outside a signed-in tab it is the page's own fetch.
 */
export function useSessionBoundFetch(): typeof fetch {
  return useContext(SessionGuardContext)?.fetch ?? rawFetch();
}

/** The same boundary for code outside React (e.g. F2's terminal clients): the currently installed guard's fetch, captured NOW. Construct the client once per company/actor/App/revision and keep it; it ends when that identity is retired. */
let installed: SessionGuardHandle | null = null;
export const sessionBoundFetch = (): typeof fetch => installed?.fetch ?? rawFetch();

/**
 * Stops this tab when the browser's session now belongs to someone else. `handle` is the guard for the
 * shown account (null while signed out); `changed`/`current` say the session was replaced.
 */
export function useSessionGuard(userId: string | undefined): {
  handle: SessionGuardHandle | null;
  changed: boolean;
  current: CurrentIdentity | null;
} {
  // `for` is the GUARD that reported, not the account: a fresh guard for the same account (A, then B, then
  // A again) never inherits an earlier guard's report.
  const [state, setState] = useState<{
    changed: boolean;
    current: CurrentIdentity | null;
    for: SessionGuard | null;
  }>({
    changed: false,
    current: null,
    for: null,
  });
  // One guard per shown account, built during render so the Chat transport can bind to it immediately.
  const handle = useMemo<SessionGuardHandle | null>(() => {
    if (!userId || typeof window === 'undefined') return null;
    const raw = rawFetch();
    const guard = createSessionGuard({
      fetchIdentity: () => readIdentity(raw),
      onChange: (identity) => setState({ changed: true, current: identity, for: guard }),
    });
    guard.setShown(userId);
    return { guard, fetch: guardFetch(raw, guard, window.location.origin) as typeof fetch };
  }, [userId]);
  // A LAYOUT effect: it runs on commit, before any child's passive effect (company, settings, inbox
  // fetch there), so the first authenticated requests carry the pair, and the render stays pure: an
  // abandoned or concurrent render never installs a replacement identity.
  useLayoutEffect(() => {
    if (!handle) return;
    const { guard } = handle;
    const raw = rawFetch();
    window.fetch = handle.fetch;
    installed = handle;
    const onFocus = () => void guard.verify();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    void guard.ready(); // fixes the company this tab shows from the coherent bootstrap
    return () => {
      // The identity this guard served is no longer shown: its actions end, terminally.
      guard.retire();
      if (window.fetch === handle.fetch) window.fetch = raw;
      if (installed === handle) installed = null;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [handle]);
  const mine = handle !== null && state.for === handle.guard;
  return { handle, changed: mine && state.changed, current: mine ? state.current : null };
}
